import { AuditEvent } from '../../../types/interchange'

interface AuditTimelineStepperProps {
  auditHistory: AuditEvent[]
}

export default function AuditTimelineStepper({ auditHistory }: AuditTimelineStepperProps) {
  const getEventIcon = (event: string) => {
    switch (event) {
      case 'EDI_MANIFEST_INGESTED':
        return '📥'
      case 'DRY_RUN_PASSED':
        return '✅'
      case 'DRY_RUN_FAILED':
        return '❌'
      case 'MANIFEST_APPROVED':
        return '🎯'
      default:
        return '📋'
    }
  }

  const getEventColor = (event: string) => {
    switch (event) {
      case 'EDI_MANIFEST_INGESTED':
        return 'border-blue-500 bg-blue-200 midnight:bg-blue-900 cyber:bg-blue-900 foundry:bg-blue-900/30 daylight:bg-blue-200'
      case 'DRY_RUN_PASSED':
        return 'border-green-500 bg-green-200 midnight:bg-green-900 cyber:bg-green-900 foundry:bg-green-900/30 daylight:bg-green-200'
      case 'DRY_RUN_FAILED':
        return 'border-red-500 bg-red-200 midnight:bg-red-900 cyber:bg-red-900 foundry:bg-red-900/30 daylight:bg-red-200'
      case 'MANIFEST_APPROVED':
        return 'border-purple-500 bg-purple-200 midnight:bg-purple-900 cyber:bg-purple-900 foundry:bg-purple-900/30 daylight:bg-purple-200'
      default:
        return 'border-gray-500 bg-gray-200 midnight:bg-gray-900 cyber:bg-gray-900 foundry:bg-gray-900/30 daylight:bg-gray-200'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString()
  }

  const formatEventName = (event: string) => {
    return event.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
  }

  return (
    <div className="flex flex-col h-full">
      <h3 className="font-bold text-muted-foreground shrink-0">Audit History Timeline</h3>

      <div className="flex-1 overflow-y-auto">
        {auditHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-full text-center py-8">
            <div className="text-4xl mb-3">📋</div>
            <div className="text-foreground font-medium">No Audit Events</div>
            <div className="text-sm text-muted-foreground mt-1">
              Audit history will appear here as events occur
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-4">
            {auditHistory.map((auditEvent, index) => (
              <div key={index} className="flex gap-4">
                {/* Timeline Line */}
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-lg border-2 ${getEventColor(auditEvent.event)}`}>
                    {getEventIcon(auditEvent.event)}
                  </div>
                  {index < auditHistory.length - 1 && (
                    <div className="w-0.5 h-full bg-border mt-2" />
                  )}
                </div>

                {/* Event Details */}
                <div className={`flex-1 p-4 border-l-4 rounded-lg ${getEventColor(auditEvent.event)}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-semibold text-foreground">
                      {formatEventName(auditEvent.event)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatTimestamp(auditEvent.timestamp)}
                    </div>
                  </div>

                  <div className="text-sm text-muted-foreground mb-2">
                    <span className="font-medium">Actor:</span> {auditEvent.actor}
                  </div>

                  {auditEvent.metadata && Object.keys(auditEvent.metadata).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="text-xs font-semibold text-muted-foreground mb-2">
                        Event Details
                      </div>
                      <div className="space-y-1">
                        {Object.entries(auditEvent.metadata).map(([key, value]) => (
                          <div key={key} className="text-xs flex justify-between">
                            <span className="text-muted-foreground">{key}:</span>
                            <span className="text-foreground font-medium">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
